/**
 * These globs can't be made dynamic, they have to be defined in this way
 * This is a requirement, done in order for Vite to be able to staticly analyze the source files
 *
 * Do _not_ refactor this to a dynamic approach, it won't work. You've been warned :-)
 */
const globFavicons = import.meta.glob<{ default: string }>(
  '../../../packages/layer-design-system/assets/icons/generated/*.ico',
  { query: '?url' },
);
const globAppleTouchIcons = import.meta.glob<{ default: string }>(
  '../../../packages/layer-design-system/assets/icons/generated/*.png',
  { query: '?url' },
);

/** The AD assets are filed under the full brand name rather than the brand code. */
function toAssetBrandCode(brandCode: string) {
  return brandCode.toLowerCase() === 'ad' ? 'algemeen-dagblad' : brandCode.toLowerCase();
}

/**
 * Do _not_ read the icon off `<link rel="apple-touch-icon">` instead: those tags are injected
 * asynchronously and are not in the DOM yet when a component mounts.
 */
export async function getBrandIconUrl(brandCode: string) {
  try {
    return await getGlobbedAsset({ brandCode: toAssetBrandCode(brandCode), type: 'apple-touch-icon' });
  } catch {
    return '';
  }
}

async function getGlobbedAsset({ brandCode, type }: { brandCode: string; type: 'apple-touch-icon' | 'favicon' }) {
  const assets = type === 'apple-touch-icon' ? globAppleTouchIcons : globFavicons;
  const extension = type === 'apple-touch-icon' ? 'png' : 'ico';

  const asset = Object.entries(assets).find(([assetPath]) => {
    const assetFilename = assetPath.split('/').pop() ?? '';
    return assetFilename.includes(`${type}-${brandCode.toLocaleLowerCase()}.${extension}`);
  });

  return (await asset?.[1]())?.default ?? '';
}

/**
 * Set the apple-touch-icon & favicon using the brand code.
 *
 * The Nuxt instance is captured synchronously and handed to `useHead` explicitly: the asset globs are
 * resolved with `await`, so by the time we reach `useHead` the setup context is gone and an implicit
 * `useNuxtApp()` would throw "A composable that requires access to the Nuxt instance was called outside...".
 */
export default async function (brandCode: string) {
  const nuxtApp = useNuxtApp();

  try {
    const brandCodeModified = toAssetBrandCode(brandCode);
    const link: ({ rel: 'apple-touch-icon'; href: string } | { rel: 'icon'; type: string; href: string })[] = [];

    const hrefAppleTouchIcon = await getGlobbedAsset({
      brandCode: brandCodeModified,
      type: 'apple-touch-icon',
    });

    if (hrefAppleTouchIcon !== '') {
      link.push({
        rel: 'apple-touch-icon',
        href: hrefAppleTouchIcon,
      });
    }

    const hrefFavicon = await getGlobbedAsset({
      brandCode: brandCodeModified,
      type: 'favicon',
    });

    if (hrefFavicon !== '') {
      link.push({
        rel: 'icon',
        type: 'image/x-icon',
        href: hrefFavicon,
      });
    }

    if (link.length === 0) {
      return;
    }

    useHead({ link }, { nuxt: nuxtApp });
  } catch {
    console.error('Failed to load favicon');
  }
}
